import { useState } from "react";
import { UserPen } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { api, fmtTime, formatApiError } from "@/lib/api";
import { useApp } from "@/lib/i18n";
import { speakerColor } from "./transcriptUtils";

export { speakerColor };

export const SpeakerDialog = ({ meetingId, names, onSaved, voice }) => {
  const { L } = useApp();
  const [draft, setDraft] = useState(names);
  const save = async () => {
    try {
      const { data } = await api.patch(`/meetings/${meetingId}/speakers`, { names: draft });
      onSaved(data.speaker_names);
      toast.success(L("Nama pembicara disimpan", "Speaker names saved"));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <Dialog onOpenChange={(o) => o && setDraft(names)}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-xl" data-testid="rename-speakers-btn"><UserPen size={14} className="mr-1" /> {L("Namakan pembicara", "Rename speakers")}</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{L("Namakan semula pembicara", "Rename speakers")}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">{voice ? L("Pembicara dikenal pasti melalui analisis suara. Masukkan nama sebenar setiap pembicara.", "Speakers were identified by voice analysis. Enter each speaker's real name.") : L("Label pembicara dianggar oleh AI berdasarkan konteks perbualan, bukan analisis suara. Sila semak.", "Speaker labels are estimated by AI from conversation context, not voice analysis. Please review.")}</p>
        <div className="space-y-3">
          {Object.keys(draft).map((k) => (
            <div key={k} className="flex items-center gap-3">
              <span className={`w-28 shrink-0 rounded-full px-2 py-1 text-center text-xs font-semibold ${speakerColor(k)}`}>{L("Pembicara", "Speaker")} {k}</span>
              <Input value={draft[k]} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })} className="rounded-xl" data-testid={`speaker-name-input-${k}`} />
            </div>
          ))}
        </div>
        <DialogFooter><Button onClick={save} className="rounded-xl bg-violet-600 hover:bg-violet-700" data-testid="save-speakers-btn">{L("Simpan", "Save")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const TranscriptList = ({ segments, names = {}, onSeek, current = -1, maxH = "max-h-[560px]" }) => (
  <div className={`slim-scroll space-y-1 overflow-y-auto ${maxH}`} data-testid="transcript-list">
    {segments.map((s, i) => {
      const active = current >= s.start && current < s.end;
      return (
        <div key={s.id || i} className={`flex gap-3 rounded-xl px-3 py-2 transition-colors ${active ? "bg-violet-50 ring-1 ring-violet-200" : "hover:bg-slate-50"}`} data-testid={`transcript-segment-${i}`}>
          <button
            onClick={() => onSeek?.(s.start)}
            className="w-14 shrink-0 pt-0.5 text-left font-mono text-xs text-violet-700 hover:underline"
            aria-label={`Pergi ke ${fmtTime(s.start)}`}
            data-testid={`segment-ts-${i}`}
          >
            {fmtTime(s.start)}
          </button>
          <div className="min-w-0">
            <span className={`mb-1 inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${speakerColor(s.speaker)}`}>{names[String(s.speaker)] || `Pembicara ${s.speaker}`}</span>
            <p className="text-sm leading-relaxed text-slate-700">{s.text}</p>
          </div>
        </div>
      );
    })}
  </div>
);

